/**
 * Snapshot of Bayan's good-type taxonomy.
 *
 * Captured from `GET /lookup/good-types` on `bayan-stg.api.elm.sa`,
 * 1 Aug 2026. Re-capture with:
 *
 * ```bash
 * curl -H "app_id: $BAYAN_APP_ID" -H "app_key: $BAYAN_APP_KEY" \
 *   https://bayan-stg.api.elm.sa/api/v1/lookup/good-types
 * ```
 *
 * ## Why a snapshot and not just the live lookup
 *
 * `BayanLookupService` caches the live tree and is the source of truth at
 * runtime. This file exists for the three jobs the live call cannot do:
 *
 * 1. **Offline `parentId` / `hasChildren`.** The previous fallback guessed
 *    parents from five id ranges taken from the V7 appendix. Those ranges
 *    stop at 60; the real taxonomy runs to 192, so every node ELM added
 *    since the guide was written resolved to "no parent, no children" —
 *    which silently disables the dangerous-goods rule for anything outside
 *    the guessed windows.
 * 2. **Build-time validation of {@link BAYAN_GOOD_TYPE_FALLBACK}.** The spec
 *    asserts every id we map to exists and is a leaf. Without it, the map
 *    rotted undetected: it pointed `liquid` at node 1, which ELM has since
 *    removed, and four more types at node 5, which grew children and is
 *    therefore no longer submittable.
 * 3. **Rendering a picker before the first live fetch resolves.**
 *
 * The taxonomy moves on ELM's timescale (months), so a stale snapshot is
 * harmless — the live cache wins whenever it is warm.
 */

/** `[id, parentId (0 = top level), nameEnglish, nameArabic]`. */
type BayanGoodTypeRow = readonly [number, number, string, string];

/**
 * Note two ELM data-entry quirks, preserved verbatim rather than corrected
 * so this file stays a faithful mirror of the upstream response:
 *
 * - **131** is `Reinforcing` in English but `الإلكترونيات` (Electronics) in
 *   Arabic. The Arabic is the correct one — its only child is "Computers,
 *   electronic and optical products".
 * - **Oils** appears three times under different parents (23 and 139 under
 *   Petroleum, 138 under Food), and **167** is absent from the sequence.
 */
const ROWS: readonly BayanGoodTypeRow[] = [
  [4, 0, 'Agricultural', 'زراعية'],
  [5, 0, 'Industrial', 'صناعية'],
  [6, 0, 'Mining or Fossilization Materials', 'مواد أعمال التعدين أو التحجير'],
  [10, 0, 'Petroleum Products', 'منتجات بترولية'],
  [11, 0, 'LIVE ANIMALS', 'حيوانات حية'],
  [12, 0, 'Food Items', 'مواد غذائية'],
  [13, 0, 'Vehicle Transportation', 'نقل المركبات'],
  [14, 12, 'Dry Fast-Moving Consum', 'سلع استهلاكية سريعة الحركة الجافة'],
  [15, 12, 'Cold Fast-Moving Consum', 'سلع استهلاكية سريعة الحركة المبردة'],
  [16, 12, 'Dry Health Care', 'مواد الرعاية الصحية الجافة'],
  [17, 12, 'Cold Health Care', 'مواد الرعاية الصحية المبردة'],
  [18, 10, 'Petrol 91', 'بنزين 91'],
  [19, 10, 'Petrol 95', 'بنزين 95'],
  [20, 10, 'Diesel', 'ديزل'],
  [21, 10, 'Kerosene', 'كيروسين'],
  [22, 10, 'Petrochemicals', 'بتروكيماويات'],
  [23, 10, 'Oils', 'زيوت'],
  [24, 10, 'Petroleum Gas', 'غاز نفطي'],
  [25, 10, 'Continent', 'قار'],
  [26, 10, 'Paraffin Wax', 'شمع برافين'],
  [27, 10, 'Asphalt', 'اسفلت'],
  [28, 10, 'Jet Fuel', 'وقود الطائرات'],
  [29, 13, 'Private Transportation For Individuals', 'النقل الخاص للافراد'],
  [30, 13, 'Vehicle Transportation For Individuals', 'نقل المركبات للافراد'],
  [31, 13, 'Private Transport For Business Sector', 'النقل الخاص لقطاع الاعمال'],
  [32, 13, 'Business Vehicle Transportation', 'نقل المركبات لقطاع الاعمال'],
  [33, 13, 'Damaged Vehicles Transportation', 'نقل المركبات المتضررة'],
  [34, 0, 'Building And Construction Items', 'مواد البناء و التشييد'],
  [35, 0, 'Dangerous Materials', 'مواد خطرة'],
  [36, 35, 'Explosives', 'المتفجرات'],
  [37, 35, 'Gases', 'الغازات'],
  [38, 35, 'Flammable Liquid', 'السوائل اللهوبة'],
  [39, 35, 'Flammable Solids', 'المواد الصلبة اللهوبة'],
  [40, 35, 'Oxidizers and organic peroxides', 'المواد المؤكسِدة والأكاسيد الفوقية العضوية'],
  [41, 35, 'Toxic and infectious substances', 'المواد السمية والمواد المعدية'],
  [42, 35, 'Radioactive material', 'المواد المشعة'],
  [43, 35, 'Corrosive substances', 'المواد الأكالة'],
  [
    44,
    35,
    'Miscellaneous dangerous substances',
    'مواد وسلع خطرة متنوعة، بما في ذلك المواد الخطرة بيئياً',
  ],
  [45, 34, 'Concrete', 'الخرسانة'],
  [46, 34, 'Steel', 'الصلب'],
  [
    47,
    34,
    'Pipes and water extension materials include tanks',
    'الأنابيب ومواد تمديد الأعمال المائية وتشمل الخزانات',
  ],
  [
    48,
    34,
    'Glass and aluminum panels include doors and windows',
    'الألواح الزجاجية والألمنيوم وتشمل الأبواب والنوافذ',
  ],
  [
    49,
    34,
    'Finishing works, paints and external textures',
    'اعمال التشطيب والدهانات والتكسيات الخارجية',
  ],
  [
    50,
    34,
    'Equipment and tools include saws, hammers, fisher',
    'المعدات والأدوات وتشمل المناشير والمطارق ومراوح اللياسة وهزازات الخرسانة',
  ],
  [51, 34, 'Plumbing work and health extensions', 'أعمال السباكة والتمديدات الصحية'],
  [52, 34, 'Insulating materials', 'مواد العزل'],
  [53, 34, 'Book and asphalt materials', 'مواد الدفان والسفلتة'],
  [
    54,
    34,
    'Woods include carpentry wood, doors and succession',
    'الأخشاب وتشمل أخشاب النجارة والأبواب وخلافة',
  ],
  [55, 34, 'Reinforcing', 'حديد التسليح'],
  [56, 34, 'Electricity extensions materials', 'مواد تمديدات الكهرباء'],
  [
    57,
    34,
    'Stones, rocks and paving materials, and this is formed tiles',
    'الأحجار والصخور ومواد الرصف ويشكل ذلك البلاط',
  ],
  [58, 34, 'Clay', 'الطين'],
  [59, 34, 'Brick', 'الطوب'],
  [
    60,
    34,
    'Air conditioning tools and machines include extension tubes and air dictations',
    'أدوات وآلات التكييف وتشمل أنابيب التمديد والدكتات الهوائية',
  ],
  [61, 6, 'Bauxite/ Aluminium Ore - AL2O3 > 40?', 'بوكسيت عالي النسبة'],
  [62, 6, 'Copper', 'النحاس'],
  [63, 6, 'Iron ore - Fe> 40?', 'خام الحديد عالي النسبة'],
  [64, 6, 'Ilmenite', 'اليمينايت'],
  [65, 6, 'Nickel', 'النيكل'],
  [66, 6, 'Niobium', 'نيوبيوم'],
  [67, 6, 'Phosphate Rock', 'الفوسفات'],
  [68, 6, 'Quartz - Silica Content > 95?', 'كوارتز'],
  [69, 6, 'Rare Earth Elements', 'العناصر الأرضية النادرة'],
  [70, 6, 'Rutile', 'روتيل'],
  [71, 6, 'Serpentinite - Gemstone variety', 'سربنتين'],
  [72, 6, 'Thorium', 'الثوريوم'],
  [73, 6, 'Tin', 'القصدير'],
  [74, 6, 'Tungsten', 'تنغستون'],
  [75, 6, 'Zinc', 'الزنك'],
  [76, 6, 'Zirconium', 'زركوينيوم'],
  [77, 6, 'Agate', 'عقيق'],
  [78, 6, 'Asbestos', 'اسبستوس'],
  [79, 6, 'Beryllium', 'بيريليوم'],
  [80, 6, 'Corundum', 'كورندوم'],
  [81, 6, 'Diamonds', 'الماس'],
  [82, 6, 'Diaspore', 'دياسبور'],
  [83, 6, 'Hafnium', 'الهفينيوم'],
  [84, 6, 'Lead', 'الرصاص'],
  [85, 6, 'Leucoxene', 'لكوكسين'],
  [86, 6, 'Lithium', 'الليثيوم'],
  [87, 6, 'Manganese', 'منغنيز'],
  [88, 6, 'Molybdenum', 'الموليبدينوم'],
  [89, 6, 'Platinum Group Metals', 'مجموعة عناصر البلاتينيوم'],
  [90, 6, 'Pyrite', 'بيريت'],
  [91, 6, 'Tantalum', 'تنتاليوم'],
  [92, 6, 'Uranium', 'يورانيوم'],
  [93, 6, 'Vanadium', 'فناديوم'],
  [94, 6, 'Xenotime', 'زينوتيم'],
  [95, 6, 'Cadmium', 'كادميوم'],
  [96, 6, 'chromium', 'كروميوم'],
  [97, 6, 'Garnet Minerals Group - Precious Stone', 'معادن مجموعة الجارنت - الأحجار الكريمة'],
  [98, 6, 'Gemstone & semi-precious stones', 'احجار كريمة وشبه الكريمة'],
  [99, 6, 'Gold', 'الذهب'],
  [100, 6, 'Jasper', 'جاسبر'],
  [101, 6, 'Olivine - Peridot/ Gemstone variety', 'اوليفين-بيردوت/نوع حجر كريم'],
  [102, 6, 'Silver', 'الفضة'],
  [103, 6, 'Unlisted mineral or metal ore', 'معادن وخامات غير مدرجة'],
  [104, 6, 'Bauxite/ Aluminium Ore - AL2O3 < 40?', 'بوكسيت منخفض النسبة'],
  [105, 6, 'Red clay', 'الطين الاحمر'],
  [106, 6, 'Bentonite', 'بنتونايت'],
  [107, 6, 'Kaolin', 'الكاولين'],
  [108, 6, 'Diatomite', 'دياتومايت'],
  [109, 6, 'Dolomite', 'دولومايت'],
  [110, 6, 'Feldspar', 'فلسبار'],
  [111, 6, 'Fluorite', 'فلورايت'],
  [112, 6, 'Garnet Minerals Group - Other Uses', 'معادن مجموعة الجارنت - استخدامات أخرى'],
  [113, 6, 'Gypsum & Anhydrite', 'جبس وانهيدرايت'],
  [114, 6, 'Iron ore - Fe<40?', 'خام الحديد منخفض النسبة'],
  [115, 6, 'Limestone for industry', 'حجر جيري للصناعة'],
  [116, 6, 'Magnesite', 'مغنيزايت'],
  [117, 6, 'Marble for industry', 'رخام للصناعة'],
  [118, 6, 'Nepheline Syenite', 'نيفلين سيانيت'],
  [119, 6, 'Perlite', 'بيرليت'],
  [120, 6, 'Potash', 'بوتاس'],
  [121, 34, 'Cement', 'أسمنت'],
  [122, 12, 'Dairy', 'الألبان'],
  [123, 11, 'Birds', 'الطيور'],
  [124, 11, 'Cattles', 'المواشي'],
  [125, 0, 'Sanitation', 'الصرف الصحي'],
  [126, 0, 'Water Tanks', 'صهاريج الماء'],
  [127, 0, 'Gas Tanks', 'صهاريج الغاز'],
  [128, 34, 'Sand', 'الرمل'],
  [129, 0, 'Spares', 'قطع غيار'],
  [130, 129, 'Used spare and scrap', 'قطع الغيار المستعملة والسكراب'],
  [131, 0, 'Reinforcing', 'الإلكترونيات'],
  [132, 131, 'Computers, electronic and optical products', 'الحواسيب والمنتجات الإلكترونية والبصرية'],
  [133, 0, 'Furniture', 'الأثاث'],
  [134, 129, 'Tires', 'الإطارات'],
  [135, 5, 'Plastic materials', 'المواد البلاستيكية'],
  [136, 0, 'Chemicals materials', 'المواد الكيميائية'],
  [137, 4, 'Forage and grain', 'الأعلاف والحبوب'],
  [138, 12, 'Oils', 'الزيوت'],
  [139, 10, 'Oils', 'الزيوت'],
  [140, 0, 'Prefabricated Houses', 'البيوت الجاهزة'],
  [141, 0, 'Waste material', 'النفايات'],
  [142, 12, 'Soft drinks', 'المشروبات الغازية'],
  [143, 5, 'Raw materials for manufacturing industries', 'المواد الأولية للصناعات التحويلية'],
  [144, 11, 'Camels and Horses', 'الجمال والأحصنة'],
  [145, 133, 'Used Furniture', 'الأثاث المستعمل'],
  [146, 133, 'New Furniture', 'الأثاث الجديد'],
  [147, 0, 'Clothes and Shoes', 'الملابس والأحذية'],
  [148, 0, 'Charcoal and Firewood', 'الفحم والحطب'],
  [149, 0, 'Medicines', 'الأدوية'],
  [150, 5, 'Leather products and related products', 'المنتجات الجلدية والمنتجات ذات الصلة'],
  [151, 5, 'Paper and its products', 'الورق ومنتجاته'],
  [152, 5, 'Rubber and plastics', 'المطاط واللدائن'],
  [153, 0, 'Veterinary medicines', 'الأدوية البيطرية'],
  [154, 0, 'Bicycles', 'الدراجات الهوائية'],
  [155, 0, 'Motorbikes', 'الدراجات النارية'],
  [156, 0, 'Medical supplies and hospital supplies', 'المستلزمات الطبية و مستلزمات المستشفيات'],
  [157, 0, 'Books', 'الكتب'],
  [158, 4, 'Agricultural materials and fertilizers', 'المواد الزراعية والأسمدة'],
  [159, 0, 'Electricity Generators', 'مولدات الكهرباء'],
  [160, 0, 'Textile and sewing materials', 'مواد النسيج والخياطة'],
  [161, 0, 'Tobacco Products', 'منتجات التبغ'],
  [162, 0, 'Remnants', 'مخلفات'],
  [163, 0, 'Cleaning materials', 'مواد تنظيف'],
  [164, 34, 'Ferrous materials', 'المواد الحديدية'],
  [165, 129, 'New spare', 'قطع غيار جديدة'],
  [166, 0, 'Bottled water', 'مياه معبأة'],
  [168, 141, 'Medical Waste', 'نفايات طبية'],
  [169, 0, 'sponges', 'الأسفنج'],
  [170, 34, 'gypsum', 'جبس'],
  [171, 12, 'Vegetables and fruits', 'الخضروات والفواكه'],
  [172, 0, 'Electrical Appliances', 'الاجهزة الكهربائية'],
  [173, 172, 'Refrigerators', 'الثلاجات'],
  [174, 172, 'Stoves', 'البوتجازات'],
  [175, 172, 'washing machines', 'الغسالات'],
  [176, 172, 'Air conditioners', 'المكيفات'],
  [177, 172, 'Ovens', 'الافران'],
  [178, 172, 'Other unclassified', 'اخري غير مصنفه'],
  [179, 0, 'containers', 'حاويات'],
  [192, 0, 'Perfumes', 'عطور'],
];

/** One node of Bayan's taxonomy, with its parent resolved. */
export type BayanGoodTypeSnapshotNode = {
  id: number;
  nameEnglish: string;
  nameArabic: string;
  /** Undefined for a top-level node. */
  parentId?: number;
  /** `true` when another node declares this one as its parent. */
  hasChildren: boolean;
};

const PARENT_IDS = new Set(ROWS.map(([, parentId]) => parentId).filter(Boolean));

/** Every known good type, keyed by id. */
export const BAYAN_GOOD_TYPE_SNAPSHOT: ReadonlyMap<number, BayanGoodTypeSnapshotNode> = new Map(
  ROWS.map(([id, parentId, nameEnglish, nameArabic]) => [
    id,
    {
      id,
      nameEnglish,
      nameArabic,
      parentId: parentId || undefined,
      hasChildren: PARENT_IDS.has(id),
    },
  ]),
);

/**
 * Whether a good type may be submitted on a waybill.
 *
 * Bayan requires the most specific node, so a parent is never valid. Ids we
 * have never seen answer `true` — refusing one would block a leaf ELM added
 * after this snapshot was taken, which is worse than letting Bayan reject it.
 */
export const isBayanGoodTypeSubmittable = (goodTypeId: number): boolean =>
  !BAYAN_GOOD_TYPE_SNAPSHOT.get(goodTypeId)?.hasChildren;
